import { motion, AnimatePresence } from "motion/react";
import { Link } from "react-router";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import { SEO } from "../components/seo";
import { useState } from "react";

// Blog post data with full content
export const posts = [
  {
    slug: "mastering-react-hooks",
    title: "Mastering React Hooks: From Basics to Advanced Patterns",
    excerpt: "Deep dive into React Hooks with practical examples and best practices for building modern React applications.",
    date: "Feb 15, 2026",
    readTime: "8 min read",
    category: "Development",
    tags: ["React", "TypeScript", "Architecture"],
    image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=400&fit=crop&fm=webp&q=75&auto=format",
    sections: [
      {
        heading: "The Hook Revolution",
        content: "React Hooks changed the way we build components forever. By allowing functional components to manage state and side effects, they simplified our codebases and made logic reuse much easier. In this guide, we'll explore how to move beyond simple useState calls into complex custom hooks that can handle everything from data fetching to complex form state."
      },
      {
        heading: "Advanced Patterns",
        content: "We'll look at the 'useReducer' hook for complex state transitions and how to combine it with 'useContext' for a lightweight state management solution that rivals Redux for many use cases. Understanding these patterns is key to building scalable React applications in 2026."
      },
      {
        heading: "Performance Optimization",
        content: "Hooks like useMemo and useCallback are powerful tools, but they are often misused. We'll discuss when to use them and when they might actually be hurting your application's performance."
      }
    ]
  },
  {
    slug: "typescript-tips-2026",
    title: "TypeScript Best Practices for Production Apps",
    excerpt: "Essential TypeScript patterns and techniques that every developer should know in 2026.",
    date: "Feb 10, 2026",
    readTime: "6 min read",
    category: "TypeScript",
    tags: ["TypeScript", "Best Practices", "Development"],
    image: "https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=800&h=400&fit=crop&fm=webp&q=75&auto=format",
    sections: [
      {
        heading: "Type Safety in 2026",
        content: "TypeScript has evolved significantly. We now have powerful features like template literal types and improved inference that allow us to write even safer code with less boilerplate. In production environments, these features are essential for maintaining large codebases."
      },
      {
        heading: "Strict Mode is Non-Negotiable",
        content: "If you're not using strict mode in 2026, you're missing out on the primary benefits of the language. We'll discuss how to migrate legacy projects to strict mode incrementally without breaking existing functionality."
      },
      {
        heading: "Utility Types and Generics",
        content: "Mastering generics and built-in utility types like Pick, Omit, and Partial can drastically reduce code duplication and make your APIs much more flexible."
      }
    ]
  },
  {
    slug: "modern-css-techniques",
    title: "Modern CSS: Grid, Flexbox, and Beyond",
    excerpt: "Explore modern CSS layout techniques and how to create responsive designs with minimal code.",
    date: "Feb 5, 2026",
    readTime: "10 min read",
    category: "Design",
    tags: ["CSS", "Design", "Frontend"],
    image: "https://images.unsplash.com/photo-1507721999472-8ed4421c4af2?w=800&h=400&fit=crop&fm=webp&q=75&auto=format",
    sections: [
      {
        heading: "Layout Mastery",
        content: "CSS Grid and Flexbox are now universally supported. We'll explore how to combine them to create complex, fluid layouts that adapt to any screen size without a single media query. The key is understanding the 'intrinsic' sizing of elements."
      },
      {
        heading: "CSS Variables and Theming",
        content: "Modern CSS variables (custom properties) allow for dynamic theming that was previously only possible with JavaScript. We'll look at how to implement dark mode and custom brand themes using only CSS."
      }
    ]
  },
  {
    slug: "full-stack-workflow",
    title: "My Full-Stack Development Workflow in 2026",
    excerpt: "A comprehensive look at the tools, frameworks, and processes I use for full-stack development.",
    date: "Jan 28, 2026",
    readTime: "12 min read",
    category: "Workflow",
    tags: ["Workflow", "Tools", "Best Practices"],
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&h=400&fit=crop&fm=webp&q=75&auto=format",
    sections: [
      {
        heading: "The Modern Stack",
        content: "My current workflow revolves around Vite, React Router 7, and Supabase. This combination provides the perfect balance of developer speed and production performance. We'll discuss how to set up a new project from scratch in under 5 minutes."
      },
      {
        heading: "AI-Assisted Coding",
        content: "AI tools have become an integral part of the development process. I'll share how I use AI for boilerplate generation, debugging, and even architectural planning without sacrificing code quality."
      }
    ]
  },
  {
    slug: "supabase-guide",
    title: "Building with Supabase: A Complete Guide",
    excerpt: "Learn how to leverage Supabase for authentication, database, and real-time features in your web apps.",
    date: "Jan 20, 2026",
    readTime: "15 min read",
    category: "Backend",
    tags: ["Supabase", "Backend", "Database"],
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop&fm=webp&q=75&auto=format",
    sections: [
      {
        heading: "Backend as a Service",
        content: "Supabase provides everything you need to build a scalable backend in minutes. We'll dive into Row Level Security (RLS) and how it simplifies your application logic by moving security rules directly into the database."
      },
      {
        heading: "Real-time Features",
        content: "One of Supabase's best features is its real-time engine. We'll build a simple live notification system to demonstrate how easy it is to sync state across multiple clients."
      }
    ]
  },
];

export function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const categories = ["All", ...Array.from(new Set(posts.map(post => post.category).filter(Boolean)))];

  const filteredPosts = activeCategory === "All" 
    ? posts 
    : posts.filter(post => post.category === activeCategory);

  return (
    <>
      <SEO 
        title="Blog - Web Development Insights" 
        description="Read articles about React, TypeScript, modern web development, and best practices from Ergys Onuzi. Learn tips and techniques for building better applications."
        canonical="/blog"
      />

      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-32 pb-20 px-6">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/3 w-96 h-96 bg-fuchsia-500 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-cyan-500 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-6xl mx-auto relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                Insights & Ideas
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Thoughts on web development, design, and building better digital experiences
              </p>
            </motion.div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-wrap gap-3 justify-center"
            >
              {categories.map((category) => (
                <button
                  key={`cat-${category}`}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 rounded-full font-semibold border transition-all duration-300 ${
                    activeCategory === category 
                      ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-transparent shadow-lg shadow-violet-500/50" 
                      : "bg-violet-950/40 text-gray-300 border-violet-500/20 hover:border-violet-400/50 hover:text-white"
                  }`}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-12 px-6 pb-32">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <AnimatePresence mode="popLayout">
                {filteredPosts.map((post, index) => (
                  <motion.div
                    key={post.slug}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="group block h-full bg-gradient-to-br from-violet-950/20 via-fuchsia-950/20 to-cyan-950/20 rounded-2xl border border-violet-500/20 hover:border-violet-400/50 overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-gradient-to-r from-violet-500/90 to-fuchsia-500/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-3 text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-violet-400 group-hover:to-fuchsia-400 group-hover:bg-clip-text transition-all duration-300">
                          {post.title}
                        </h3>
                        <p className="text-gray-400 mb-4 line-clamp-2 text-sm">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(post.date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {post.readTime}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-violet-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                          {post.tags.slice(0, 3).map((tag) => (
                            <span
                              key={`${post.slug}-${tag}`}
                              className="flex items-center gap-1 px-2 py-1 bg-violet-950/40 text-violet-400 text-[10px] rounded-full"
                            >
                              <Tag className="w-2.5 h-2.5" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
            
            {filteredPosts.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-500 text-xl">No posts found in this category.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}